import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useState } from 'react';
import { useSnackbar } from 'notistack';

import {
  Channel,
  extractBackendErrorMessage,
  getBackendErrorStatus,
  getGupshupApp,
  GupshupAppResolution,
  postHsmLine,
} from '@/services/hsm-backend';

export interface LineSetupModalGupshupProps {
  channel: Channel;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Gupshup line setup in 2 steps:
 * 1. The executive enters the number and clicks "Verificar" → resolves the app
 *    in the Gupshup Partner API (`getGupshupApp`). 200 shows appName/appId in
 *    green and enables the alta; 422 shows the inline error + a "Reintentar".
 * 2. "Dar de alta línea" persists the line (`postHsmLine` with `{ phone }`).
 */
export const LineSetupModalGupshup = ({
  channel,
  onClose,
  onSuccess,
}: LineSetupModalGupshupProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [phone, setPhone] = useState(channel.phone ?? '');
  const [resolution, setResolution] = useState<GupshupAppResolution | null>(
    null
  );
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const verified = resolution !== null;

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyError(null);
    setResolution(null);
    try {
      const result = await getGupshupApp(channel.channelId, phone.trim());

      setResolution(result);
    } catch (error) {
      setVerifyError(extractBackendErrorMessage(error));
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await postHsmLine(channel.channelId, { phone: phone.trim() });
      onSuccess();
    } catch (error) {
      const status = getBackendErrorStatus(error);

      if (status === 409) {
        enqueueSnackbar('Esta línea ya está dada de alta', {
          variant: 'error',
        });
      } else {
        // 422 or others: the number stopped resolving — verify again.
        setResolution(null);
        setVerifyError(extractBackendErrorMessage(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Editing the number invalidates the previous verification.
  const handlePhoneChange = (value: string) => {
    setPhone(value);
    setResolution(null);
    setVerifyError(null);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Dar de alta línea Gupshup
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ fontFamily: 'ui-monospace, monospace' }}
        >
          {channel.name} · {channel.channelId}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Número de WhatsApp"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              error={Boolean(verifyError)}
              helperText={
                verifyError ? 'El número no resolvió en Partner API.' : ' '
              }
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={() => void handleVerify()}
              disabled={!phone.trim() || verifying}
              sx={{ mt: 1, whiteSpace: 'nowrap' }}
            >
              {verifyError ? 'Reintentar' : 'Verificar'}
            </Button>
          </Stack>

          {verified && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              App encontrada en Gupshup. Confirmá antes de dar de alta.
              <Box
                sx={{
                  mt: 1,
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  columnGap: 1.5,
                  rowGap: 0.5,
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 12,
                }}
              >
                <span>appName</span>
                <strong>{resolution.appName}</strong>
                <span>appId</span>
                <strong>{resolution.appId}</strong>
              </Box>
            </Alert>
          )}

          {verifyError && <Alert severity="error">{verifyError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!verified || submitting}
        >
          Dar de alta línea
        </Button>
      </DialogActions>
    </Dialog>
  );
};
